import { Select } from "antd";
import clsx from 'clsx';
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import query from "../../../../../../../server/constants.api";
import useFetch from "../../../../../../../server/useFetch";
import FamilyTypeFilter from "../../../ItemsConfigurations/Filters/FamilyTypeFilter/FamilyTypeFilter";
import { filtersTypes, getFamilyTypeFilterForApi } from "../../../ItemsConfigurations/ItemsConfig/ItemsConfig";
import { thresholdsReturnOptions } from "../../ThresholdsConfig/ThresholdsConfig";

const useThresholdsCreateModal = ({ setSelectedItem, subTenants, selectedTenant, setSelectedTenant, isToCleanUp }) => {
    const { t } = useTranslation();
    const [selectedFamilyOrType, setSelectedFamilyOrType] = useState({ selected: null, value: null });

    const {
        data: familyOrType,
        refetch: refetchFamilyOrType,
        isFetching: isFetchingFamilyOrType,
        remove: removeFamilyOrTypeData
    } = useFetch(
        '/items/distinct-properties',
        'getDistinctPropertiesForThresholds',
        {
            tenant: selectedTenant,
            properties: "family,type",
        },
        false,
        null,
        query.middlewareURL)

    const {
        data: itemsData,
        refetch: refetchItems,
        isFetching: isFetchingItems,
        remove: removeItemsData
    } = useFetch('/items',
        'getItemsDataForThresholds',
        {
            tenant: selectedTenant,
            ...getFamilyTypeFilterForApi(selectedFamilyOrType)
        },
        false,
        null,
        query.middlewareURL
    );

    /**
     * Refetch the items when the family or type is selected
     */
    useEffect(() => {
        if (selectedFamilyOrType.selected && selectedFamilyOrType.value) {
            refetchItems();
        }
        setSelectedItem(null);
    }, [selectedFamilyOrType])

    /**
     * Set the selected family or type to null when the family or type is fetching
     * This means that the user selected a different tenant
     */
    useEffect(() => {
        if (familyOrType && !isFetchingFamilyOrType) {
            setSelectedFamilyOrType({ selected: filtersTypes.family, value: null });
        }
    }, [isFetchingFamilyOrType])

    /**
     * Refetch the family and type when the tenant is selected
     * Remove the selected item
     * Remove the items data
     */
    useEffect(() => {
        if (selectedTenant) {
            refetchFamilyOrType();
            setSelectedItem(null);
            removeItemsData();
        }
    }, [selectedTenant])

    useEffect(() => {
        if (isToCleanUp) {
            setSelectedFamilyOrType({ selected: null, value: null });
            removeFamilyOrTypeData();
            removeItemsData();
        }
    }, [isToCleanUp])

    const ThresholdsSelectModalConfig = [
        {
            title: t('1. Choose Affected Resource'),
            dataIndex: 'title',
            text: (<h3>{t('1. Choose Affected Resource')}</h3>),
            fieldTypeId: 1,
            size: 24,
        },
        {
            title: t('Tenant'),
            dataIndex: 'tenant',
            fieldTypeId: 6,
            required: true,
            size: 12,
            customObject: () => {
                return (
                    <Select
                        listHeight={200}
                        size={'large'}
                        bordered={false}
                        placeholder={<span style={{ color: "lightgrey" }}>{t("Select Value")}</span>}
                        options={thresholdsReturnOptions('tenant', { subTenants })}
                        onChange={(e, record) => { setSelectedTenant(e); }}
                        className={clsx('select-atomic', 'full-width')} />
                )
            }
        },
        {
            dataIndex: 'TenantFamilyType',
            fieldTypeId: 6,
            required: true,
            size: 12,
            customObject: () => {
                return (
                    <FamilyTypeFilter
                        familyOptions={thresholdsReturnOptions('tenantfamilytype', familyOrType?.family)}
                        typeOptions={thresholdsReturnOptions('tenantfamilytype', familyOrType?.type)}
                        isFilterDisabled={isFetchingFamilyOrType || !selectedTenant}
                        isForModal={true}
                        selectedFilter={selectedFamilyOrType}
                        setSelectedFilter={setSelectedFamilyOrType}
                        isDefaultSelectionRequired={false}
                    />
                )
            },
        },
        {
            title: 'Table',
            dataIndex: 'Table',
            fieldTypeId: 9,
            listData: itemsData ?? [],
            isVisible: itemsData,
            isLoading: isFetchingItems,
            tableConfig: [
                {
                    title: t('Name'),
                    dataIndex: 'name',
                },
                {
                    title: t('Type'),
                    dataIndex: 'type',
                },
                {
                    title: t('Family'),
                    dataIndex: 'family',
                },
                {
                    title: t('Class'),
                    dataIndex: 'class',
                },
                {
                    title: t('Layer'),
                    dataIndex: 'layer',
                },
            ],
            hasCheckbox: false,
            onRowSelect: (record, selected, selectedRows) => {
                setSelectedItem(record)
            },
            cleanRowSelectedOnDataChanged: true
        },
    ]

    return ThresholdsSelectModalConfig;
}

export default useThresholdsCreateModal;